import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HorizontalResizableContainerWrapper,
  ResizableHorizontal,
} from '@ardoq/resizable';
import { TodayLine, TodayTriangle } from '../xAxis/styles';
import {
  MOUSE_HOVER_THROTTLE_DELAY,
  TIMELINE_PHASES_COLUMN_MINIMUM_WIDTH_RATIO,
  UNSET_POPVERS_ARGS,
} from './consts';
import styled from 'styled-components';
import { CrosshairX } from '../xAxis/CrosshairX';
import { panAndZoomScrollbar } from './panAndZoomScrollbar';
import { XGridLines } from '../grid/xGridLines';
import {
  LABELS_COLUMN_BACKGROUND_COLOR,
  TIMELINES_ITEM_ROW_HEIGHT,
  Y_AXIS_MARGIN_TIMELINE,
  X_AXIS_MARGIN_TIMELINE,
} from '../items/consts';
import { ItemLabel } from '../items/labels/itemLabels';
import { ItemPhases } from '../items/phases/itemPhases';
import { TimelineChartAreaArgs } from '../types';
import { getEventTargetEntityId } from '../items/utils';
import { PopoverAtPoint } from '@ardoq/popovers';
import {
  getPopoverState,
  handleMouseMove,
  type TimelinePopoverState,
} from './eventsUtils';
import { throttle } from 'lodash';

const ChartAreaContainer = styled.div<{
  $isPanning: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: ${TIMELINES_ITEM_ROW_HEIGHT}px;
  background-color: white;
  .forExport & {
    overflow-y: hidden;
  }

  cursor: ${({ $isPanning }) => ($isPanning ? 'grabbing' : 'default')};
`;

const ItemsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  flex-wrap: nowrap;
`;

const LabelsParentContainer = styled.div`
  background-color: ${LABELS_COLUMN_BACKGROUND_COLOR};
  padding: ${X_AXIS_MARGIN_TIMELINE}px ${Y_AXIS_MARGIN_TIMELINE}px;
  position: relative;
  overflow: hidden;
`;

const PhasesParentContainerResizableStylesWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
`;

const PhasesParentContainer = styled.div`
  position: relative;
  flex: 1;
`;

const ChartArea = ({
  renderedItemLabels,
  renderedItemPhases,
  renderedItemTops,
  backgroundScrollHeight,
  transitLinksById,
  hasGrouping,
  contextMenuDataAttribute,
  isPanning,
  containerRef,
  timelineChartProperties,
  viewInstanceId,
  componentSingleClickHandler,
  componentDoubleClickHandler,
  commands,
}: TimelineChartAreaArgs) => {
  const {
    labelsColumnMinWidth,
    adjustedGridPrecision,
    todayPosition,
    dimensions,
    xAxisProps: { bottomLabelRowProps },
    crosshairXProps,
    hoveredFieldsPopoverProps,
    hoveredMainLabelPopoverProps,
    hoveredOtherLabelPopoverProps,
  } = timelineChartProperties;

  const [popover, setPopover] = useState<TimelinePopoverState | null>(null);

  useEffect(() => {
    setPopover(
      getPopoverState({
        hoveredFieldsPopoverProps,
        hoveredMainLabelPopoverProps,
        hoveredOtherLabelPopoverProps,
      })
    );
  }, [
    hoveredFieldsPopoverProps,
    hoveredMainLabelPopoverProps,
    hoveredOtherLabelPopoverProps,
  ]);

  const {
    margin: { left: marginLeft },
    chart: { width: chartWidth },
  } = dimensions;
  const chartAreaWidth = marginLeft + chartWidth;

  const showToday =
    todayPosition >= marginLeft && todayPosition <= chartAreaWidth;
  const lastItemIndex = renderedItemLabels?.length - 1;

  const phasesColumnMinWidth = useMemo(
    () =>
      Math.min(
        chartAreaWidth * TIMELINE_PHASES_COLUMN_MINIMUM_WIDTH_RATIO,
        // 100 is arbitrary, for the phases column to not vanish completely if such edge case ever happens
        Math.max(chartAreaWidth - labelsColumnMinWidth, 100)
      ),
    [chartAreaWidth, labelsColumnMinWidth]
  );

  // event handlers
  const labelSingleClickHandler = useCallback(
    (e: React.MouseEvent) => {
      if (!componentSingleClickHandler) {
        return;
      }
      const entityId = getEventTargetEntityId(e, contextMenuDataAttribute);
      if (entityId) {
        componentSingleClickHandler(entityId);
      }
    },
    [contextMenuDataAttribute, componentSingleClickHandler]
  );
  const labelDoubleClickHandler = useCallback(
    (e: React.MouseEvent) => {
      if (!componentDoubleClickHandler) {
        return;
      }
      const entityId = getEventTargetEntityId(e, contextMenuDataAttribute);
      if (entityId) {
        componentDoubleClickHandler(entityId);
      }
    },
    [contextMenuDataAttribute, componentDoubleClickHandler]
  );

  const throttledSetMouseHover = useMemo(
    () => throttle(commands.setMouseHover, MOUSE_HOVER_THROTTLE_DELAY),
    [commands]
  );

  const chartAreaMouseMove = useCallback(
    /**
     * All that is related to mouse hover: popovers, crosshairX display, TODO hover highlighting.
     * We send the graph node id, the hovered element type and the mouse coordinates to state,
     * where we have everything we could possibly need to resolve and compute any props.
     */
    (event: React.MouseEvent) =>
      handleMouseMove(
        { setMouseHover: throttledSetMouseHover, viewInstanceId },
        event
      ),
    [throttledSetMouseHover, viewInstanceId]
  );

  const chartAreaMouseLeave = useCallback(() => {
    throttledSetMouseHover({
      ...UNSET_POPVERS_ARGS,
      viewInstanceId,
    });
  }, [throttledSetMouseHover, viewInstanceId]);
  const chartAreaScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
      commands.setChartAreaScrollTop(e, viewInstanceId);
    },
    [commands, viewInstanceId]
  );

  return (
    <ChartAreaContainer
      ref={containerRef}
      onScroll={chartAreaScroll}
      onMouseMove={chartAreaMouseMove}
      onMouseLeave={chartAreaMouseLeave}
      $isPanning={isPanning}
      style={{ maxHeight: backgroundScrollHeight }}
    >
      <ItemsContainer
        style={{ width: chartAreaWidth, maxHeight: backgroundScrollHeight }}
        onMouseLeave={() => {
          commands.setHoveredItem(null, viewInstanceId);
        }}
      >
        {/**
         * minWidth and initialWidth properties of ResizableHorizontal children control the resizable sections width.
         * Style properties ensure that the width of elements, that are defining the phases alignment, complies with
         * the chart dimensions. We recalculate the chart dimensions once on resizeEnd and update the columns size.
         */}
        <ResizableHorizontal
          onResizeStart={() => {
            commands.setIsTimelineColumnsResizeActive(true, viewInstanceId);
          }}
          onResizeEnd={args => {
            commands.handleLabelsColumnResize(args, viewInstanceId);
            commands.setIsTimelineColumnsResizeActive(false, viewInstanceId);
          }}
        >
          <HorizontalResizableContainerWrapper
            minWidth={labelsColumnMinWidth}
            initialWidth={marginLeft}
          >
            <LabelsParentContainer
              style={{ width: marginLeft, height: backgroundScrollHeight }}
              onDoubleClick={labelDoubleClickHandler}
              onClick={labelSingleClickHandler}
            >
              {renderedItemLabels.map((label, index) => (
                <ItemLabel
                  key={`${label.itemModel.id}`}
                  itemLabelData={label}
                  hasGrouping={hasGrouping}
                  isLastItem={index === lastItemIndex}
                  contextMenuDataAttribute={contextMenuDataAttribute}
                  viewInstanceId={viewInstanceId}
                  containerWidth={marginLeft}
                  renderedItemTops={renderedItemTops}
                  rowIndex={index}
                  transitLinksById={transitLinksById}
                  commands={commands}
                />
              ))}
            </LabelsParentContainer>
          </HorizontalResizableContainerWrapper>
          <HorizontalResizableContainerWrapper minWidth={phasesColumnMinWidth}>
            <PhasesParentContainerResizableStylesWrapper>
              <PhasesParentContainer
                style={{
                  maxWidth: chartWidth,
                  minWidth: chartWidth,
                  height: backgroundScrollHeight,
                }}
              >
                <XGridLines
                  positions={bottomLabelRowProps.tickLabelsToDisplay.map(
                    tick => tick.tickPosition
                  )}
                  dimensions={dimensions}
                >
                  {renderedItemPhases.map((phases, index) => {
                    return (
                      <ItemPhases
                        key={`${phases.itemModel.id}-phases-container`}
                        itemPhases={phases}
                        chartDimensions={dimensions}
                        isLastItem={index === lastItemIndex}
                        contextMenuDataAttribute={contextMenuDataAttribute}
                        gridPrecision={adjustedGridPrecision}
                        viewInstanceId={viewInstanceId}
                        containerWidth={chartWidth}
                        renderedItemTops={renderedItemTops}
                        rowIndex={index}
                        commands={commands}
                      />
                    );
                  })}
                </XGridLines>
              </PhasesParentContainer>
            </PhasesParentContainerResizableStylesWrapper>
          </HorizontalResizableContainerWrapper>
        </ResizableHorizontal>
      </ItemsContainer>
      {crosshairXProps && !isPanning ? (
        <CrosshairX {...crosshairXProps} />
      ) : null}
      {showToday && (
        <>
          <TodayTriangle style={{ left: todayPosition }} />
          <TodayLine
            style={{
              left: todayPosition,
              height: backgroundScrollHeight,
            }}
          />
        </>
      )}
      {popover && (
        <PopoverAtPoint
          point={popover.point}
          isOpen
          onClose={() => setPopover(null)}
          placement={popover.placement}
        >
          {popover.content}
        </PopoverAtPoint>
      )}
    </ChartAreaContainer>
  );
};

export const chartArea = panAndZoomScrollbar(ChartArea);
